import { subscribeConsent, canMeasure, canMeasureFunnel } from "../measurement/consent";
import { safePath, sanitizeAnalyticsUrl, sanitizeProperties } from "../measurement/measurementCore";
import { config } from "../config";
import { getOrCreateTrackingUuid } from "./trackingIdentity";
export { getOrCreateTrackingUuid } from "./trackingIdentity";

type WatchTelemetry = Record<string, unknown> & {
  funnelId?: "story-watch" | "story-slim";
  declineCode?: string;
};

type TrackingPayload = {
  uid: string;
  ts?: number;
  lp?: string;
  lq?: string;
  at: string;
  seq?: number;
  total?: number;
  val?: string;
  lid?: number;
  watch?: WatchTelemetry;
};

const QUEUE_KEY = "parables:tracking:pending:v3";
const MAX_AGE = 24 * 60 * 60 * 1000;
type Pending = { payload: TrackingPayload; queuedAt: number };
let queue: Pending[] = [];
const canSend = (payload: TrackingPayload) => canMeasure() || (canMeasureFunnel()
  && (payload.at === "milestone" && payload.val?.startsWith("story-slim/a/") || payload.at === "lead_link"));
try {
  const saved: unknown = canMeasureFunnel() ? JSON.parse(localStorage.getItem(QUEUE_KEY) ?? "[]") : [];
  if (Array.isArray(saved)) queue = saved.filter(item => item?.payload?.uid && canSend(item.payload) && typeof item.queuedAt === "number" && Date.now() - item.queuedAt < MAX_AGE).slice(-200);
} catch { /* Storage is optional. */ }
let flushing = false;
let activeRequest: AbortController | undefined;
subscribeConsent(() => { if (!canMeasureFunnel()) { queue = []; reportedMilestones.clear(); activeRequest?.abort(); window.clearTimeout(retryTimer); localStorage.removeItem(QUEUE_KEY); } });
let retryTimer: number | undefined;
let retryDelay = 1000;
const persist = () => {
  if (!canMeasureFunnel()) return;
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(queue)); } catch { /* Keep the in-memory queue. */ }
};
const flush = async () => {
  if (flushing || !config.enableFunnelMetrics || !navigator.onLine) return;
  flushing = true;
  try {
    while (queue.length && canMeasureFunnel()) {
      const next = queue[0];
      if (!canSend(next.payload)) { queue.shift(); persist(); continue; }
      if (Date.now() - next.queuedAt > MAX_AGE) { queue.shift(); persist(); continue; }
      activeRequest = new AbortController();
      const response = await fetch(`${config.apiBaseUrl}/bizops/customer`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(next.payload), keepalive: true,
        signal: AbortSignal.any([activeRequest.signal, AbortSignal.timeout(10000)])
      });
      if (!response.ok && (response.status >= 500 || response.status === 429)) throw new Error("Retry tracking");
      // Non-retryable validation errors must not block later events.
      if (queue[0] === next) queue.shift();
      persist(); retryDelay = 1000;
    }
  } catch {
    if (!canMeasureFunnel()) return;
    window.clearTimeout(retryTimer);
    retryTimer = window.setTimeout(() => void flush(), retryDelay);
    retryDelay = Math.min(retryDelay * 2, 30000);
  } finally { flushing = false; }
};
export const reportFunnelAction = (payload: TrackingPayload): void => {
  if (!canSend(payload)) return;
  // Persist only the bounded legacy contract; no raw query or answer values.
  payload = { ...payload, ...(payload.lp ? {lp: safePath(payload.lp)} : {}),
    ...(payload.lq ? {lq: new URL(sanitizeAnalyticsUrl('/' + payload.lq), 'https://analytics.invalid').search} : {}),
    ...(payload.watch ? {watch: sanitizeProperties(payload.watch)} : {}) };
  if (!queue.some(item => payload.watch
    ? item.payload.watch?.eventId === payload.watch.eventId
    : item.payload.uid === payload.uid && item.payload.at === payload.at && item.payload.val === payload.val && item.payload.seq === payload.seq)) {
    queue.push({ payload: { ...payload, ts: Date.now() }, queuedAt: Date.now() });
    queue = queue.slice(-200); persist();
  }
  void flush();
};
let activeScope = "";
const reportedMilestones = new Set<string>();
export const setTrackingScope = (scope: string) => { activeScope = scope; };
export const reportMilestone = (stage: string) => {
  if (!canMeasureFunnel() || !activeScope || reportedMilestones.has(`${activeScope}/${stage}`)) return;
  reportedMilestones.add(`${activeScope}/${stage}`);
  reportFunnelAction({ uid: getOrCreateTrackingUuid(), at: "milestone", val: `${activeScope}/${stage}`,
    lp: window.location.pathname, lq: window.location.search || undefined });
};
window.addEventListener("online", () => void flush());
window.addEventListener("pageshow", () => void flush());
window.addEventListener("pagehide", () => {
  persist();
  if (!config.enableFunnelMetrics) return;
  // Dispatch queued events before teardown. Keep them queued until a normal request
  // acknowledges them; the server deduplicates retries using the action identity.
  let bytes = 0;
  for (const item of queue) {
    if (!canSend(item.payload)) continue;
    const body = JSON.stringify(item.payload);
    bytes += new TextEncoder().encode(body).byteLength;
    if (bytes > 48000) break;
    void fetch(`${config.apiBaseUrl}/bizops/customer`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true
    }).catch(() => {});
  }
});
void flush();
